import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, Link, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { ArrowRightIcon } from '@classity-edtech/ui-base/dist/components/Displays/Icons';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { globalFonts } from '@classity-edtech/ui-base/dist/style/source/typography';

export interface RatingPromoProps {
  avatars?: string[];
  rating?: number;
  reviewCount?: number;
  offerText?: string;
  offerSubtext?: string;
  showOffer?: boolean;
  useSprite?: boolean;
  spriteUrl?: string;
  spriteSize?: number;
  spriteColumns?: number;
}

export const RatingPromo = ({
  avatars = [],
  rating = 4.9,
  reviewCount = 2654,
  offerText = 'Money Back Guarantee',
  offerSubtext = 'At anytime during your first week',
  showOffer = false,
  useSprite = false,
  spriteUrl = 'https://static04.classity.co/web/public/avatars-sprite.png',
  spriteSize = 124,
  spriteColumns = 7,
}: RatingPromoProps) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const renderStars = () => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Box
          key={i}
          component='img'
          src='https://www.figma.com/api/mcp/asset/2c2aa51b-619e-48c9-b42a-7d83007977a1.svg'
          sx={{
            width: '100%',
            height: '100%',
          }}
        />,
      );
    }
    return stars;
  };

  const getAvatarStyles = (index: number) => {
    const avatarWidth = 40;
    const totalAvatars = 14;
    const backgroundPosition = `-${index * avatarWidth}px 0`;
    return {
      display: 'inline-block',
      width: '40px',
      height: '40px',
      backgroundImage: `url(${spriteUrl})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: `${totalAvatars * avatarWidth}px 40px`,
      borderRadius: '50%',
      backgroundPosition,
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        alignItems: 'center',
        maxWidth: isMobile ? '400px' : '600px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body3'
          sx={{
            position: 'relative',
            zIndex: 2,
            color: globalColours['$global-colour-content-primary'],
            ...globalFonts['$global-font-body-medium'],
            textAlign: 'center',
          }}
        >
          We are on the lookout for the best teachers to{' '}
          <Link
            to={`/${webPaths.teach}`}
            component={RouterLink}
            sx={{
              display: 'inline-block',
              position: 'relative',
              zIndex: 2,
              alignItems: 'flex-start',
              cursor: 'pointer',
              pointerEvents: 'auto',
              color: globalColours['$global-colour-interactive-control-default'],
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: globalColours['$global-colour-interactive-control-hover'],
              },
            }}
          >
            Join our platform
          </Link>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {useSprite && spriteUrl ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {Array.from({ length: isMobile ? 9 : 14 }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    marginRight: index === (isMobile ? 8 : 13) ? '0' : '-10px',
                    ...getAvatarStyles(index),
                  }}
                />
              ))}
            </Box>
          ) : (
            avatars.slice(0, isMobile ? 9 : 14).map((avatar, index) => (
              <Box
                key={index}
                sx={{
                  marginRight: index === avatars.length - 1 ? '0' : '-10px',
                  position: 'relative',
                  width: '40px',
                  height: '40px',
                }}
              >
                <Box
                  component='img'
                  src={avatar}
                  sx={{
                    position: 'absolute',
                    zIndex: 0,
                    top: '-95%',
                    left: '-85%',
                    right: '-115%',
                    bottom: '-80%',
                    width: '124px',
                    height: '124px',
                  }}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>
      {showOffer && (
        <Box
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '427px',
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                cursor: 'pointer',
                height: '26px',
                width: '158px',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '83.33%',
                  bottom: 0,
                  left: 0,
                }}
              >
                {renderStars()[0]}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '62.5%',
                  bottom: 0,
                  left: '20.83%',
                }}
              >
                {renderStars()[1]}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '41.67%',
                  bottom: 0,
                  left: '41.67%',
                }}
              >
                {renderStars()[2]}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: '20.83%',
                  bottom: 0,
                  left: '62.5%',
                }}
              >
                {renderStars()[3]}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: '83.33%',
                }}
              >
                {renderStars()[4]}
              </Box>
            </Box>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                ...globalFonts['$global-font-body-medium'],
              }}
            >
              from {reviewCount.toLocaleString()} happy users
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              height: '52px',
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                gap: '4px',
                alignItems: 'flex-start',
                overflow: 'clip',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  overflow: 'clip',
                  position: 'relative',
                  width: '32px',
                  height: '32px',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '7.15%',
                    left: '9.95%',
                    right: '7.14%',
                    bottom: '10.71%',
                  }}
                >
                  <ArrowRightIcon
                    sx={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant='body3SemiBold'
                sx={{
                  color: globalColours['$global-colour-content-secondary'],
                }}
              >
                {offerText}
              </Typography>
            </Box>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                ...globalFonts['$global-font-body-medium'],
              }}
            >
              {offerSubtext}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

RatingPromo.propTypes = {
  avatars: PropTypes.arrayOf(PropTypes.string),
  rating: PropTypes.number,
  reviewCount: PropTypes.number,
  offerText: PropTypes.string,
  offerSubtext: PropTypes.string,
  showOffer: PropTypes.bool,
  useSprite: PropTypes.bool,
  spriteUrl: PropTypes.string,
  spriteSize: PropTypes.number,
  spriteColumns: PropTypes.number,
};

export default styled<any>(RatingPromo)(
  ({ theme }) => `
`,
);
